import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Search, Loader2, Info, Link2, Save, Contact, Building2, Users, ShieldCheck, ShieldOff, AlertTriangle, Check, Upload } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { toast } from "sonner";

const CRIMSON = "#bf3b4f";

export const KIND = {
  contact: {
    title: "Contacts", icon: Contact, link: "contact", display: "name1",
    list: api.contacts, create: api.createContact, update: api.updateContact, reverse: api.contactParticipants,
    masterName: (m) => m.par_contact_display_name || m.name,
    masterFields: [
      { k: "first_name", l: "First name", req: 1 }, { k: "last_name", l: "Last name", req: 1 },
      { k: "preferred_name", l: "Preferred name" }, { k: "phone_number", l: "Phone", req: 1 },
      { k: "email", l: "Email", req: 1 }, { k: "status", l: "Status", opts: ["Active", "Inactive", "Deceased"] },
    ],
    prefill: (m) => ({ contact: m.name, name1: m.par_contact_display_name, phone: m.phone_number, email: m.email }),
    childFields: [{ k: "relationship", l: "Relationship" }, { k: "reference_number", l: "Reference #" }, { k: "phone", l: "Phone" }, { k: "email", l: "Email" }],
    childBools: [{ k: "emergency_contact", l: "Emergency" }, { k: "legal_guardian", l: "Guardian" }, { k: "decision_maker", l: "Decision maker" }, { k: "informal_decision_maker", l: "Informal decision maker" }, { k: "has_consent", l: "Consent signed" }],
  },
  stakeholder: {
    title: "Stakeholders", icon: Building2, link: "stakeholder", display: "stakeholder_display",
    list: api.stakeholders, create: api.createStakeholder, update: api.updateStakeholder, reverse: api.stakeholderParticipants,
    masterName: (m) => m.stake_display_name || m.name,
    masterFields: [
      { k: "first_name", l: "First name", req: 1 }, { k: "last_name", l: "Last name", req: 1 },
      { k: "organisation", l: "Organisation" }, { k: "profession", l: "Profession", req: 1 },
      { k: "phone", l: "Phone" }, { k: "email", l: "Email" },
      { k: "preferred_contact_method", l: "Preferred contact", req: 1 }, { k: "provider_number", l: "NDIS provider #" },
      { k: "website", l: "Website" }, { k: "status", l: "Status", opts: ["Active", "Inactive"] },
      { k: "additional_information", l: "Additional information", area: 1, rich: 1 },
    ],
    prefill: (m) => ({ stakeholder: m.name, stakeholder_display: m.stake_display_name, organisation: m.organisation, profession: m.profession, phone_number: m.phone, email: m.email, website: m.website }),
    childFields: [{ k: "profession", l: "Profession" }, { k: "organisation", l: "Organisation" }, { k: "preferred_contact", l: "Preferred contact" }, { k: "phone_number", l: "Phone" }, { k: "email", l: "Email" }],
    childBools: [{ k: "has_consent", l: "Consent signed" }],
    childNote: { k: "stakeholder_relationship_info", l: "Relationship notes" },
  },
};

export function LinksSection({ kindKey, rows, setRows, canEdit, participant }) {
  const cfg = KIND[kindKey];
  const Icon = cfg.icon;
  const navigate = useNavigate();
  const dirTab = kindKey === "contact" ? "contacts" : "stakeholders";
  const [addOpen, setAddOpen] = useState(false);
  const [master, setMaster] = useState(null); // master doc name being viewed/edited
  const [removeIdx, setRemoveIdx] = useState(null); // pending removal (consent on file)
  const [withdrawIdx, setWithdrawIdx] = useState(null); // row index for withdraw dialog

  const linkedNames = new Set(rows.map((r) => r[cfg.link]).filter(Boolean));
  const toggleBools = cfg.childBools.filter((b) => b.k !== "has_consent");

  const linkMaster = (m) => {
    setRows([...rows, cfg.prefill(m)]);
    setAddOpen(false);
    toast.success(`${cfg.masterName(m)} linked.`);
  };
  const setRow = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const patchRow = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const doRemove = (i) => { setRows(rows.filter((_, j) => j !== i)); setRemoveIdx(null); };
  const requestRemove = (i) => { if (rows[i]?.has_consent) setRemoveIdx(i); else doRemove(i); };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {cfg.title}</div>
        {canEdit && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddOpen(true)} data-testid={`add-${kindKey}`}><Plus className="h-3.5 w-3.5 mr-1" />Add {kindKey}</Button>}
      </div>

      <div className="space-y-2.5" data-testid={`${kindKey}-rows`}>
        {rows.length === 0 && <div className="text-xs text-slate-400">No {cfg.title.toLowerCase()} linked.</div>}
        {rows.map((r, i) => (
          <div key={i} className="rounded-md border border-slate-200 p-2.5" data-testid={`${kindKey}-row-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              {r[cfg.link] ? (
                <button
                  type="button"
                  onClick={() => navigate(`/directory?tab=${dirTab}&open=${encodeURIComponent(r[cfg.link])}`)}
                  className="text-sm font-medium text-slate-800 hover:text-rose-700 hover:underline text-left"
                  title="Open this record in the Directory"
                  data-testid={`${kindKey}-open-dir-${i}`}
                >
                  {r[cfg.display] || r[cfg.link]}
                </button>
              ) : (
                <span className="text-sm font-medium text-slate-800">{r[cfg.display] || `${cfg.title} ${i + 1}`}</span>
              )}
              {r[cfg.link] && <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-[9px] py-0">{r[cfg.link]}</Badge>}
              {r.consent_withdrawn
                ? <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[9px] py-0 gap-0.5" title={r.consent_withdrawn_reason || ""} data-testid={`${kindKey}-withdrawn-badge-${i}`}><ShieldOff className="h-2.5 w-2.5" />Consent withdrawn</Badge>
                : r.has_consent
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] py-0 gap-0.5" data-testid={`${kindKey}-consent-badge-${i}`}><ShieldCheck className="h-2.5 w-2.5" />Consent signed</Badge>
                  : null}
              <div className="flex-1" />
              {r[cfg.link] && <button onClick={() => setMaster(r[cfg.link])} className="text-slate-400 hover:text-indigo-600" title="View record & linked participants" data-testid={`${kindKey}-view-${i}`}><Info className="h-4 w-4" /></button>}
              {canEdit && <button onClick={() => requestRemove(i)} className="text-slate-400 hover:text-rose-600" title="Unlink" data-testid={`${kindKey}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {cfg.childFields.map((f) => (
                <div key={f.k}>
                  <Label className="text-[10px] text-slate-500">{f.l}</Label>
                  <Input value={r[f.k] || ""} disabled={!canEdit} onChange={(e) => setRow(i, f.k, e.target.value)} className="mt-0.5 h-8 text-xs" data-testid={`${kindKey}-${i}-${f.k}`} />
                </div>
              ))}
            </div>
            {(toggleBools.length > 0 || r.has_consent) && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {toggleBools.map((b) => {
                  const on = !!r[b.k];
                  return (
                    <button key={b.k} type="button" disabled={!canEdit} onClick={() => setRow(i, b.k, !on)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border transition-colors ${on ? "bg-rose-50 border-rose-200 text-rose-700 font-medium" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"} ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                      data-testid={`${kindKey}-${i}-${b.k}`}>
                      {on && <Check className="h-3 w-3" />}{b.l}
                    </button>
                  );
                })}
                {r.has_consent && canEdit && (
                  <button type="button" onClick={() => setWithdrawIdx(i)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors ml-auto"
                    data-testid={`${kindKey}-withdraw-btn-${i}`}>
                    <ShieldOff className="h-3 w-3" />Consent withdrawn
                  </button>
                )}
              </div>
            )}
            {cfg.childNote && (
              <div className="mt-2">
                <Label className="text-[10px] text-slate-500">{cfg.childNote.l}</Label>
                <Textarea value={r[cfg.childNote.k] || ""} disabled={!canEdit} onChange={(e) => setRow(i, cfg.childNote.k, e.target.value)} className="mt-0.5 text-xs min-h-[44px]" />
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Links & relationship details save with the participant (Save button up top).</p>

      {addOpen && <AddDialog cfg={cfg} kindKey={kindKey} onClose={() => setAddOpen(false)} onLink={linkMaster} linkedNames={linkedNames} canEdit={canEdit} />}
      {master && <MasterDialog cfg={cfg} kindKey={kindKey} name={master} onClose={() => setMaster(null)} canEdit={canEdit}
        onSaved={(m) => setRows(rows.map((r) => (r[cfg.link] === m.name ? { ...r, ...cfg.prefill(m) } : r)))} />}
      {removeIdx !== null && (
        <RemoveWarnDialog cfg={cfg} kindKey={kindKey} row={rows[removeIdx]}
          onCancel={() => setRemoveIdx(null)} onConfirm={() => doRemove(removeIdx)} />
      )}
      {withdrawIdx !== null && (
        <WithdrawDialog kindKey={kindKey} participant={participant} row={rows[withdrawIdx]}
          onClose={() => setWithdrawIdx(null)}
          onDone={(patch) => { patchRow(withdrawIdx, patch); setWithdrawIdx(null); }} />
      )}
    </div>
  );
}

function AddDialog({ cfg, kindKey, onClose, onLink, linkedNames, canEdit }) {
  const [tab, setTab] = useState("link");
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ status: "Active" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { cfg.list().then(setList).catch(() => setList([])); }, []);

  const filtered = (list || []).filter((m) => cfg.masterName(m).toLowerCase().includes(q.toLowerCase()));

  const create = async () => {
    for (const f of cfg.masterFields) if (f.req && !String(form[f.k] || "").trim()) { toast.error(`${f.l} is required.`); return; }
    setBusy(true);
    try {
      const r = await cfg.create(form);
      const m = r[kindKey] || { name: r.name, ...form };
      // ensure display name for prefill
      if (!m[kindKey === "contact" ? "par_contact_display_name" : "stake_display_name"])
        m[kindKey === "contact" ? "par_contact_display_name" : "stake_display_name"] = `${form.first_name || ""} ${form.last_name || ""}`.trim();
      onLink(m);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`add-${kindKey}-dialog`}>
        <DialogHeader><DialogTitle className="text-base font-brand">Add {kindKey}</DialogTitle></DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="link" data-testid={`${kindKey}-tab-link`}>Link existing</TabsTrigger>
            <TabsTrigger value="create" data-testid={`${kindKey}-tab-create`}>Create new</TabsTrigger>
          </TabsList>

          <TabsContent value="link" className="mt-3">
            <div className="relative mb-2">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cfg.title.toLowerCase()}…`} className="h-9 pl-8 text-sm" data-testid={`${kindKey}-link-search`} autoFocus />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1" data-testid={`${kindKey}-link-list`}>
              {list === null && <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
              {list && filtered.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No matches. Create a new one instead.</div>}
              {filtered.map((m) => {
                const already = linkedNames.has(m.name);
                return (
                  <div key={m.name} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-2.5 py-1.5">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-slate-800 truncate">{cfg.masterName(m)} <span className="text-slate-400 font-normal">· {m.name}</span></div>
                      <div className="text-[10px] text-slate-500 truncate">{[m.organisation, m.profession, m.phone_number || m.phone, m.email].filter(Boolean).join(" · ")}</div>
                    </div>
                    <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" disabled={already} onClick={() => onLink(m)} data-testid={`${kindKey}-link-${m.name}`}>
                      <Link2 className="h-3.5 w-3.5 mr-1" />{already ? "Linked" : "Link"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="create" className="mt-3">
            <div className="grid sm:grid-cols-2 gap-2.5">
              {cfg.masterFields.map((f) => (
                <div key={f.k} className={f.area ? "sm:col-span-2" : ""}>
                  <Label className="text-[11px] font-semibold text-slate-600">{f.l}{f.req ? <span className="text-rose-500"> *</span> : null}</Label>
                  {f.opts ? (
                    <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                      <SelectTrigger className="mt-1 h-9 text-sm" data-testid={`${kindKey}-new-${f.k}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{f.opts.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.rich ? (
                    <RichTextEditor value={form[f.k] || ""} onChange={(v) => setForm({ ...form, [f.k]: v })} testid={`${kindKey}-new-${f.k}`} />
                  ) : f.area ? (
                    <Textarea value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1 text-sm min-h-[54px]" data-testid={`${kindKey}-new-${f.k}`} />
                  ) : (
                    <Input value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1 h-9 text-sm" data-testid={`${kindKey}-new-${f.k}`} />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={create} disabled={busy || !canEdit} style={{ backgroundColor: CRIMSON }} data-testid={`${kindKey}-create-submit`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Create & link
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function MasterDialog({ cfg, kindKey, name, onClose, onSaved, canEdit }) {
  const [form, setForm] = useState(null);
  const [linked, setLinked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    cfg.list().then((all) => setForm(all.find((m) => m.name === name) || { name })).catch(() => setForm({ name }));
    cfg.reverse(name).then(setLinked).catch(() => setLinked([]));
  }, [name]);

  const save = async () => {
    for (const f of cfg.masterFields) if (f.req && !String(form[f.k] || "").trim()) { toast.error(`${f.l} is required.`); return; }
    setBusy(true);
    try {
      const body = Object.fromEntries(cfg.masterFields.map((f) => [f.k, form[f.k] ?? ""]));
      const r = await cfg.update(name, body);
      const m = r[kindKey] || { ...form, name };
      toast.success("Record updated.");
      onSaved(m);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`${kindKey}-master-dialog`}>
        <DialogHeader><DialogTitle className="text-base font-brand">{cfg.title.slice(0, -1)} · {name}</DialogTitle></DialogHeader>
        {!form ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {cfg.masterFields.map((f) => (
                <div key={f.k} className={f.area ? "sm:col-span-2" : ""}>
                  <Label className="text-[11px] font-semibold text-slate-600">{f.l}{f.req ? <span className="text-rose-500"> *</span> : null}</Label>
                  {f.opts ? (
                    <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                      <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{f.opts.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.rich ? (
                    <RichTextEditor value={form[f.k] || ""} disabled={!canEdit} onChange={(v) => setForm({ ...form, [f.k]: v })} testid={`${kindKey}-master-${f.k}`} />
                  ) : f.area ? (
                    <Textarea value={form[f.k] || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1 text-sm min-h-[54px]" />
                  ) : (
                    <Input value={form[f.k] || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1 h-9 text-sm" data-testid={`${kindKey}-master-${f.k}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-md border border-slate-100 bg-slate-50/60 p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5"><Users className="h-3 w-3" /> Linked participants ({linked?.length ?? "…"})</div>
              <div className="flex flex-wrap gap-1.5" data-testid={`${kindKey}-linked-participants`}>
                {linked === null && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                {linked && linked.length === 0 && <span className="text-[11px] text-slate-400">Not linked to any participant yet.</span>}
                {(linked || []).map((p) => <Badge key={p.name} className="bg-white text-slate-600 border-slate-200 text-[10px]">{p.display_name}</Badge>)}
              </div>
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={onClose}>Close</Button>
              {canEdit && <Button onClick={save} disabled={busy} style={{ backgroundColor: CRIMSON }} data-testid={`${kindKey}-master-save`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save record
              </Button>}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RemoveWarnDialog({ cfg, kindKey, row, onCancel, onConfirm }) {
  const label = row?.[cfg.display] || (kindKey === "contact" ? "this contact" : "this stakeholder");
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid={`${kindKey}-remove-warn-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base font-brand flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />Remove a consented {kindKey}?
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-600 space-y-2">
          <p><span className="font-medium text-slate-800">{label}</span> has <span className="font-medium text-emerald-700">consent on file</span>.</p>
          <p>Unlinking them here only removes them from this participant. It does <span className="font-medium">not</span> record a consent withdrawal. If consent has genuinely been withdrawn, use the <span className="font-medium">Consent withdrawn</span> action instead so the evidence and reason are captured.</p>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onCancel} data-testid={`${kindKey}-remove-cancel`}>Keep linked</Button>
          <Button onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700" data-testid={`${kindKey}-remove-confirm`}>
            <Trash2 className="h-4 w-4 mr-1.5" />Unlink anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WithdrawDialog({ kindKey, participant, row, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null); // { name, dataUrl }
  const [busy, setBusy] = useState(false);
  const label = row?.name1 || row?.stakeholder_display || (kindKey === "contact" ? "this contact" : "this stakeholder");

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File must be under 10 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, dataUrl: reader.result });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required to withdraw consent."); return; }
    if (!participant || !row?.name) { toast.error("Save the participant before withdrawing consent for a new record."); return; }
    setBusy(true);
    try {
      await api.withdrawConsent(participant, {
        kind: kindKey, row: row.name, reason: reason.trim(),
        evidence_filename: file?.name || null, evidence_data: file?.dataUrl || null,
      });
      toast.success("Consent withdrawal recorded.");
      onDone({ has_consent: 0, consent_withdrawn: 1, consent_withdrawn_reason: reason.trim() });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record withdrawal."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={`${kindKey}-withdraw-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base font-brand flex items-center gap-2">
            <ShieldOff className="h-4 w-4 text-amber-600" />Withdraw consent
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Recording a consent withdrawal for <span className="font-medium text-slate-800">{label}</span>. This writes immediately to the participant record and cannot be undone from here.</p>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Reason<span className="text-rose-500"> *</span></Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is consent being withdrawn?" className="mt-1 text-sm min-h-[64px]" data-testid={`${kindKey}-withdraw-reason`} autoFocus />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Evidence (optional)</Label>
            <div className="mt-1 flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:border-slate-300 cursor-pointer" data-testid={`${kindKey}-withdraw-evidence-label`}>
                <Upload className="h-3.5 w-3.5" />Attach file
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={pickFile} data-testid={`${kindKey}-withdraw-evidence`} />
              </label>
              {file && <span className="text-[11px] text-slate-500 truncate max-w-[180px]">{file.name}</span>}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Stored privately against the participant.</p>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} data-testid={`${kindKey}-withdraw-cancel`}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid={`${kindKey}-withdraw-submit`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldOff className="h-4 w-4 mr-1.5" />}Record withdrawal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
